import { jsx as _jsx } from "react/jsx-runtime";
import { createRichTextComponentNode, normalizeComponent, RichTextNodeType, traverseComponent, validateTraverseNodeForPublish, validateTraverseNodeForSave, } from '@dossierhq/core';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext.js';
import { DecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode.js';
import { $getNodeByKey, createCommand, } from 'lexical';
import { useCallback, useContext, useMemo } from 'react';
import { DossierContext } from '../../contexts/DossierContext.js';
import { ComponentFieldEditorWithoutClear } from '../EntityEditor/ComponentFieldEditor.js';
import { RichTextEditorContext } from './RichTextEditorContext.js';
export function $createAdminComponentNode(data) {
    return new AdminComponentNode(data);
}
/** @public */
export function $isAdminComponentNode(node) {
    return node instanceof AdminComponentNode;
}
export const INSERT_ADMIN_COMPONENT_COMMAND = createCommand();
function AdminComponentComponent({ className, format, nodeKey, data, }) {
    const [editor] = useLexicalComposerContext();
    const { adapter, schema: schema } = useContext(DossierContext);
    const { adminOnly: richTextAdminOnly } = useContext(RichTextEditorContext);
    const componentSpec = schema?.getComponentTypeSpecification(data.type);
    const adminOnly = richTextAdminOnly || !!componentSpec?.adminOnly;
    const setValue = useCallback((value) => {
        editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            if ($isAdminComponentNode(node)) {
                node.setData(value);
            }
        });
    }, [editor, nodeKey]);
    const validationIssues = useMemo(() => {
        return validateComponent(schema, adminOnly, data);
    }, [schema, adminOnly, data]);
    const overriddenEditor = adapter.renderRichTextComponentEditor({
        value: data,
        validationIssues,
        onChange: setValue,
    });
    return (_jsx(BlockWithAlignableContents, { className: className, format: format, nodeKey: nodeKey, children: overriddenEditor ?? (_jsx(ComponentFieldEditorWithoutClear, { className: "rich-text-item-indentation", adminOnly: adminOnly, value: data, validationIssues: validationIssues, onChange: setValue })) }));
}
// TODO this is duplicated from the validation in validateField() in EntityEditorReducer
// the main reason is that it's tricky to match the `path` in those validation issues with a certain
// rich text node. We most likely need to change that validation to use the Lexical nodes instead
// (as opposed to the serialized nodes which lack the node key)
// Hopefully we can get rid on RichTextEditorContext and `adminOnly` parameters to the field editors
function validateComponent(schema, adminOnly, value) {
    const errors = [];
    if (schema) {
        const normalizeResult = normalizeComponent(schema, [], value);
        const valueToValidate = normalizeResult.isOk() ? normalizeResult.value : value;
        for (const node of traverseComponent(schema, [], valueToValidate)) {
            const error = validateTraverseNodeForSave(schema, node);
            if (error) {
                errors.push(error);
            }
        }
        if (!adminOnly) {
            const publishedSchema = schema.toPublishedSchema();
            for (const node of traverseComponent(publishedSchema, [], valueToValidate)) {
                const error = validateTraverseNodeForPublish(schema, node);
                if (error) {
                    errors.push(error);
                }
            }
        }
    }
    return errors;
}
export class AdminComponentNode extends DecoratorBlockNode {
    __data;
    static getType() {
        return RichTextNodeType.component;
    }
    static clone(node) {
        return new AdminComponentNode(node.__data, node.__format, node.__key);
    }
    constructor(data, format, key) {
        super(format, key);
        this.__data = data;
    }
    setData(data) {
        const self = this.getWritable();
        self.__data = data;
    }
    getData() {
        const self = this.getLatest();
        return self.__data;
    }
    static importJSON(serializedNode) {
        const node = $createAdminComponentNode(serializedNode.data);
        node.setFormat(serializedNode.format);
        return node;
    }
    exportJSON() {
        //TODO format
        return createRichTextComponentNode(this.__data);
    }
    createDOM() {
        const div = document.createElement('div');
        div.style.display = 'contents';
        return div;
    }
    getTextContent() {
        return '\n';
    }
    decorate(_editor, config) {
        const embedBlockTheme = config.theme.embedBlock || {};
        const className = {
            base: embedBlockTheme.base || '',
            focus: embedBlockTheme.focus || '',
        };
        return (_jsx(AdminComponentComponent, { className: className, data: this.__data, format: this.__format, nodeKey: this.__key }));
    }
}
//# sourceMappingURL=AdminComponentNode.js.map